import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Cpu,
  Database,
  Download,
  HelpCircle,
  Settings as SettingsIcon,
  Sliders,
  Upload,
} from "feather-icons-react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import type { PaygapMetricItemDto } from "@green0meter/api-client";

import {
  usePaygapData,
  usePaygapParameters,
  useAutoPopulateAnalysisParams,
} from "../PaygapUploadData/hooks";
import { useGradeEstimatorState } from "../PaygapUploadData/components/PaygapDataUpload/hooks";
import {
  GradeEstimatorDrawer,
  GradeOverwriteDialog,
  AnalysisParamsOverwriteDialog,
} from "../PaygapUploadData/components/PaygapDataUpload/components";
import templateConfig from "../PaygapUploadData/components/PaygapDataUpload/paygapTemplateConfig.json";
import {
  AnalysisParametersDrawer,
  EmployeeDataDrawer,
  PaygapAnalysisActions,
} from "../PaygapUploadData/components";
import {
  PaygapPayDifferenceGauge,
  PaygapAnalysisOverview,
  PaygapIncomeQuartileAnalysis,
  PaygapSalaryCostAnalysis,
  PaygapEmployeeDistribution,
  PaygapPayGradesOverview,
  PaygapPositionRoleOverview,
  PaygapStatisticalIndicators,
  PaygapBlinderOaxaca,
} from "../PaygapAnalysis/components";
import { GradeChart, PositionChart } from "../PaygapAnalysis/components/PaygapPositionRoleOverview/components";
import { BarChart } from "../PaygapAnalysis/components/PaygapPayGradesOverview/components";
import { usePaygapAnalysisData } from "../PaygapAnalysis/hooks";
import PaygapWelcomeBanner from "../PaygapWelcomeBanner";

import { useGetCurrentCompanyPersonnel } from "@api/hooks/paygap";
import { useUserStore } from "@store/user";
import { Button, Card, CardContent, Grid } from "@components/base";
import CardGuide from "@components/common/CardGuide/CardGuide";

// ── Collapsible Rollup ──

interface RollupProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  whyText?: string;
  description?: string;
}

const Rollup: FC<RollupProps> = ({ title, defaultOpen = false, children, whyText, description }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ cursor: "pointer" }}
          onClick={(): void => setOpen((prev) => !prev)}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            <Typography variant="h6">{title}</Typography>
            {whyText && (
              <Tooltip title={whyText}>
                <IconButton size="small" onClick={(e): void => e.stopPropagation()}>
                  <HelpCircle size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Collapse in={open}>
          <Box pt={2}>
            {description && (
              <Box
                sx={{
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  mb: 2,
                  borderLeft: 3,
                  borderColor: "primary.main",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              </Box>
            )}
            {children}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ── Main Hub View ──

const PaygapHubView: FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const analysisRef = useRef<HTMLDivElement>(null);
  const dataInputRef = useRef<HTMLDivElement>(null);
  const gradeModellingRef = useRef<HTMLDivElement>(null);
  const runAnalysisRef = useRef<HTMLDivElement>(null);

  // Guide step state
  const [activeGuideStep, setActiveGuideStep] = useState<number | undefined>(undefined);
  const [guideClicked, setGuideClicked] = useState<boolean[] | undefined>(undefined);

  const handleGuideStepClick = (index: number): void => {
    setActiveGuideStep(activeGuideStep === index ? undefined : index);
    if (!guideClicked?.[index]) {
      setGuideClicked((prev) =>
        prev ? prev.map((c, i) => (i === index ? true : c)) : [0, 1, 2].map((i) => i === index),
      );
    }
  };

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>): void => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── State ──
  const [analysisResults, setAnalysisResults] = useState<PaygapMetricItemDto[] | null>(null);
  const [isEmployeeDrawerOpen, setIsEmployeeDrawerOpen] = useState(false);
  const [autoFetchData, setAutoFetchData] = useState(false);
  const [isAnalysisParametersOpen, setIsAnalysisParametersOpen] = useState(false);
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<null | HTMLElement>(null);

  // ── Hooks ──
  const { personnelData } = useGetCurrentCompanyPersonnel(user, true);
  const paygapData = usePaygapData();
  const {
    uploadStatus,
    employeeData,
    hasManualData,
    isPendingUploadFile,
    processFileUpload,
    handleImportData,
    setEmployeeData,
    importSuccess,
    importPersonnelError,
    dataFetchedFromDatabase,
    setDataFetchedFromDatabase,
  } = paygapData;

  const paygapParams = usePaygapParameters();
  const { parameters, hasIdConflicts, isLoading: isLoadingParameters } = paygapParams;

  const autoPopulate = useAutoPopulateAnalysisParams();
  const processedData = usePaygapAnalysisData(analysisResults);

  const gradeEstimator = useGradeEstimatorState({
    employeeData,
    setEmployeeData,
    handleImportData,
  });

  // Auto-populate after import
  const hasTriggeredAutoPopulate = useRef(false);

  useEffect(() => {
    if (importSuccess && !hasTriggeredAutoPopulate.current) {
      hasTriggeredAutoPopulate.current = true;
      autoPopulate.checkAndPopulate();
    }
    if (!importSuccess) {
      hasTriggeredAutoPopulate.current = false;
    }
  }, [importSuccess, autoPopulate]);

  const hasValidData =
    uploadStatus.status === "success" ||
    hasManualData ||
    employeeData.length > 0 ||
    (Array.isArray(personnelData) && personnelData.length > 0);

  const hasResults = Boolean(analysisResults && processedData);

  // ── Callbacks ──
  const handleAnalysisComplete = useCallback((results: PaygapMetricItemDto[]): void => {
    setAnalysisResults(results);
    setTimeout(() => {
      analysisRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  const handleManageEmployeeData = useCallback((): void => {
    setAutoFetchData(true);
    setIsEmployeeDrawerOpen(true);
  }, []);

  // ── File upload ──
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (employeeData.length > 0 || hasManualData) {
      setShowOverwriteWarning(true);
      setPendingFile(file);

      return;
    }

    await processFileUpload(file);
  };

  const handleOverwriteConfirm = async (): Promise<void> => {
    if (pendingFile) {
      await processFileUpload(pendingFile);
      setPendingFile(null);
    }
    setShowOverwriteWarning(false);
  };

  const handleOverwriteCancel = (): void => {
    setPendingFile(null);
    setShowOverwriteWarning(false);
    const fileInput = document.getElementById("csv-upload") as HTMLInputElement;

    if (fileInput) fileInput.value = "";
  };

  // ── Template downloads ──
  const openTemplateMenu = (e: React.MouseEvent<HTMLButtonElement>): void =>
    setTemplateMenuAnchor(e.currentTarget);
  const closeTemplateMenu = (): void => setTemplateMenuAnchor(null);

  const handleDownloadCsvTemplate = (): void => {
    const currentLang = i18n.language?.split("-")[0] ?? "en";
    const lang = (currentLang === "en" || currentLang === "cs" || currentLang === "sk") ? currentLang : "en";
    const config = templateConfig[lang as keyof typeof templateConfig];
    const csvTemplate = `${config.headers.join(",")}\n${config.sampleRow.join(",")}`;
    const blob = new Blob([csvTemplate], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", "paygap_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    closeTemplateMenu();
  };

  const handleDownloadExcelTemplate = (): void => {
    const currentLang = i18n.language?.split("-")[0] ?? "en";
    const lang = (currentLang === "en" || currentLang === "cs" || currentLang === "sk") ? currentLang : "en";
    const config = templateConfig[lang as keyof typeof templateConfig];
    const worksheet = XLSX.utils.aoa_to_sheet([config.headers, config.sampleRow]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Paygap Template");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = "paygap_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    closeTemplateMenu();
  };

  const guideSteps: React.ComponentProps<typeof CardGuide>["steps"] = [
    {
      title: t("common.stepNumber", { step: 1 }),
      description: t("paygap.hub.step1.title", "Upload & Configure Data"),
      detail: {
        title: t("paygap.hub.step1.detail", "Upload your employee data file and configure analysis parameters such as reference year, currency, and education levels."),
        points: [
          t("paygap.hub.step1.point1", "Supported formats: CSV, XLSX, XLS"),
          t("paygap.hub.step1.point2", "Configure education years for accurate regression analysis"),
          t("paygap.hub.step1.point3", "Set grading referentials for job classification"),
        ],
      },
      button: {
        text: t("paygap.hub.goToDataInput", "Go to Data Input"),
        onClick: (): void => scrollTo(dataInputRef),
      },
      tooltip: {
        title: t("paygap.hub.step1.tooltipTitle", "Data Input"),
        points: [
          t("paygap.hub.step1.tooltip1", "Upload employee data with salary, gender, age, and job information."),
          t("paygap.hub.step1.tooltip2", "Configure analysis parameters including education year mappings."),
        ],
      },
    },
    {
      title: t("common.stepNumber", { step: 2 }),
      description: t("paygap.hub.step2.title", "Grade Modelling"),
      detail: {
        title: t("paygap.hub.step2.detail", "Use the Grade Estimator to classify job positions by complexity, effort, responsibility, and working conditions."),
        points: [
          t("paygap.hub.step2.point1", "AI-powered grade estimation based on structured questionnaire"),
          t("paygap.hub.step2.point2", "Apply grades to employees for equal pay analysis"),
        ],
      },
      button: {
        text: t("paygap.hub.goToGradeModelling", "Go to Grade Modelling"),
        onClick: (): void => scrollTo(gradeModellingRef),
      },
      tooltip: {
        title: t("paygap.hub.step2.tooltipTitle", "Grade Modelling"),
        points: [
          t("paygap.hub.step2.tooltip1", "Estimate job grades if you don't have your own grading system."),
          t("paygap.hub.step2.tooltip2", "Grades are used to group employees for like-for-like pay comparison."),
        ],
      },
    },
    {
      title: t("common.stepNumber", { step: 3 }),
      description: t("paygap.hub.step3.title", "Run Analysis"),
      detail: {
        title: t("paygap.hub.step3.detail", "Run the pay gap analysis to calculate adjusted and unadjusted gender pay differences across your organization."),
        points: [
          t("paygap.hub.step3.point1", "Results include pay gap per grade, quartile analysis, and statistical testing"),
          t("paygap.hub.step3.point2", "Download PDF and Excel reports for EU Pay Transparency Directive compliance"),
        ],
      },
      button: {
        text: hasResults
          ? t("paygap.hub.restartAnalysis", "Restart Analysis")
          : t("paygap.hub.goToRunAnalysis", "Run Analysis"),
        onClick: (): void => scrollTo(runAnalysisRef),
      },
      tooltip: {
        title: t("paygap.hub.step3.tooltipTitle", "Analysis"),
        points: [
          t("paygap.hub.step3.tooltip1", "The analysis uses multivariate regression to calculate the adjusted gender pay gap."),
          t("paygap.hub.step3.tooltip2", "Results are typically available within 15 minutes."),
        ],
      },
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* ────── Welcome Banner ────── */}
      <Grid container>
        <PaygapWelcomeBanner />
      </Grid>

      {/* ────── Equal Pay Analysis Guide ────── */}
      <CardGuide
        title={t("paygap.hub.guideTitle", "Equal Pay Analysis Guide")}
        titleTooltip={t("paygap.hub.guideTooltip", "Follow these steps to complete your pay gap analysis")}
        onClickStep={handleGuideStepClick}
        active={activeGuideStep}
        steps={guideSteps}
        clicked={guideClicked}
        rightTitleContent={
          <Button
            variant="contained"
            size="small"
            onClick={(): void => scrollTo(runAnalysisRef)}
          >
            {hasResults
              ? t("paygap.hub.restartAnalysis", "Restart Analysis")
              : t("paygap.hub.runAnalysis", "Run Analysis")}
          </Button>
        }
      />

      {/* ────── Rollup 1: Data Input ────── */}
      <div ref={dataInputRef}>
        <Rollup
          title={t("paygap.hub.dataInput", "Data Input")}
          defaultOpen
          whyText={t(
            "paygap.hub.dataInputWhy",
            "Upload your employee salary data and configure analysis parameters. Accurate data is the foundation of reliable pay gap analysis.",
          )}
          description={t(
            "paygap.hub.dataInputDesc",
            "Start by downloading the template, fill it with your employee data (salary, gender, age, education, job grade), then upload it. Configure analysis parameters to set the reference year, currency, and education level mappings.",
          )}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2} flexWrap="wrap">
              {/* Download Template */}
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={openTemplateMenu}
              >
                {t("paygap.uploadData.templateButton", "Download Template")}
              </Button>

              {/* Upload Data */}
              <input
                type="file"
                accept=".csv, .xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileUpload}
                style={{ display: "none" }}
                id="csv-upload"
              />
              <label htmlFor="csv-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload size={16} />}
                  disabled={uploadStatus.status === "uploading" || isPendingUploadFile}
                >
                  {t("paygap.uploadData.uploadButton", "Upload Data")}
                </Button>
              </label>

              {/* Manage Data */}
              <Button
                variant="outlined"
                startIcon={<Database size={16} />}
                onClick={handleManageEmployeeData}
              >
                {t("paygap.uploadData.manageDataButton", "Manage Data")}
                {hasManualData && ` (${employeeData.length})`}
              </Button>

              {/* Analysis Parameters with education tooltip */}
              <Tooltip
                title={t(
                  "paygap.hub.paramsTooltip",
                  "Configure reference year, currency, and education levels. The number of years for each education level is required for accurate pay gap regression analysis.",
                )}
              >
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon size={16} />}
                  onClick={(): void => setIsAnalysisParametersOpen(true)}
                >
                  {t("paygap.parameters.button", "Analysis Parameters")}
                </Button>
              </Tooltip>

              {/* Template download menu */}
              <MuiMenu
                anchorEl={templateMenuAnchor}
                open={Boolean(templateMenuAnchor)}
                onClose={closeTemplateMenu}
              >
                <MuiMenuItem onClick={handleDownloadCsvTemplate}>
                  {t("paygap.downloadCSV", "Download CSV")}
                </MuiMenuItem>
                <MuiMenuItem onClick={handleDownloadExcelTemplate}>
                  {t("paygap.downloadExcel", "Download Excel")}
                </MuiMenuItem>
              </MuiMenu>
            </Box>

            {/* Overwrite warning */}
            {showOverwriteWarning && (
              <Alert
                severity="warning"
                action={
                  <Box display="flex" gap={1}>
                    <Button variant="outlined" size="small" onClick={handleOverwriteCancel}>
                      {t("paygap.dialog.cancel", "Cancel")}
                    </Button>
                    <Button variant="contained" size="small" onClick={handleOverwriteConfirm}>
                      {t("paygap.dialog.overwrite", "Overwrite")}
                    </Button>
                  </Box>
                }
              >
                <Typography variant="body2">
                  <strong>{t("paygap.upload.overwriteWarning", "This will replace your existing data.")}</strong>
                </Typography>
              </Alert>
            )}

            {/* Upload progress */}
            {(uploadStatus.status === "uploading" || isPendingUploadFile) && (
              <Box>
                <LinearProgress />
                <Typography variant="body2" mt={1}>
                  {t("paygap.upload.uploading", "Uploading...")}
                </Typography>
              </Box>
            )}

            {/* Upload success */}
            {uploadStatus.status === "success" && uploadStatus.validationResults && (
              <Alert severity="success" icon={<CheckCircle size={16} />}>
                <Typography variant="body2">{uploadStatus.message}</Typography>
                <Typography variant="body2" mt={0.5}>
                  {t("paygap.upload.rows", {
                    count: uploadStatus.validationResults.rowCount,
                    defaultValue: `${uploadStatus.validationResults.rowCount} rows imported`,
                  })}
                </Typography>
              </Alert>
            )}

            {/* Upload error */}
            {uploadStatus.status === "error" && (
              <Alert severity="error" icon={<AlertCircle size={16} />}>
                {uploadStatus.message}
              </Alert>
            )}

            {/* Existing manual data */}
            {hasManualData && uploadStatus.status !== "success" && (
              <Alert severity="info" icon={<Database size={16} />}>
                <Typography variant="body2">
                  {t("paygap.upload.manualEntries", {
                    count: employeeData.length,
                    defaultValue: `${employeeData.length} employee records available`,
                  })}
                </Typography>
              </Alert>
            )}

            {/* Run Analysis */}
            <div ref={runAnalysisRef}>
              <PaygapAnalysisActions
                startAnalysisButtonText={t("paygap.uploadData.startAnalysisButton", "Run Analysis")}
                parameters={parameters}
                hasValidData={hasValidData}
                hasIdConflicts={hasIdConflicts}
                importPersonnelError={importPersonnelError}
                dataFetchedFromDatabase={dataFetchedFromDatabase}
                onAnalysisComplete={handleAnalysisComplete}
                onManageEmployeeData={handleManageEmployeeData}
                onOpenAnalysisParameters={(): void => setIsAnalysisParametersOpen(true)}
                isLoadingParameters={isLoadingParameters}
              />
            </div>
          </Box>
        </Rollup>
      </div>

      {/* ────── Rollup 2: Grade Modelling ────── */}
      <div ref={gradeModellingRef}>
        <Rollup
          title={t("paygap.hub.gradeModelling", "Grade Modelling")}
          whyText={t(
            "paygap.hub.gradeModellingWhy",
            "Job grades group positions by complexity, effort, and responsibility. They are essential for comparing pay between men and women in equivalent roles.",
          )}
          description={t(
            "paygap.hub.gradeModellingDesc",
            "If your organization does not have its own job grading system, use the Grade Estimator to classify positions based on a structured questionnaire. The Grade Optimizer (coming soon) will help fine-tune grades to minimize pay gap variance.",
          )}
        >
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<Cpu size={16} />}
              onClick={(): void => gradeEstimator.setShowGradeEstimator(true)}
            >
              {t("paygap.gradeEstimator.button", "Grade Estimator")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Sliders size={16} />}
              disabled
            >
              {t("paygap.gradeOptimizer.button", "Grade Optimizer")}
            </Button>
          </Box>
        </Rollup>
      </div>

      {/* ────── Analysis Results (only visible when data exists) ────── */}
      {hasResults && processedData && analysisResults && (
        <>
          <div ref={analysisRef}>
            {/* Rollup 4: Simple Breakdown (default open) */}
            <Box display="flex" flexDirection="column" gap={3}>
              <Rollup
                title={t("paygap.hub.simpleBreakdown", "Simple Breakdown and Reports")}
                defaultOpen
                whyText={t(
                  "paygap.hub.simpleBreakdownWhy",
                  "The key pay gap metrics and overall analysis summary.",
                )}
              >
                <Box display="flex" flexDirection="column" gap={3}>
                  <PaygapPayDifferenceGauge
                    payDifferenceData={processedData.payDifferenceData}
                    payDifferenceDataMultiple={processedData.payDifferenceDataMultiple}
                  />
                  <PaygapAnalysisOverview
                    analysisSummary={processedData.analysisSummary}
                    processedData={processedData}
                  />
                </Box>
              </Rollup>

              {/* Rollup 5: Detailed Breakdown */}
              <Rollup
                title={t("paygap.hub.detailedBreakdown", "Detailed Breakdown")}
                whyText={t(
                  "paygap.hub.detailedBreakdownWhy",
                  "Income quartile analysis, salary cost distribution, and employee demographics help identify where pay gaps originate.",
                )}
              >
                <Box display="flex" flexDirection="column" gap={3}>
                  <PaygapIncomeQuartileAnalysis
                    incomeQuartileData={processedData.incomeQuartileData}
                    unadjustedPayGap={processedData.unadjustedPayGap}
                  />
                  <PaygapSalaryCostAnalysis
                    salaryCostData={processedData.salaryCostData}
                  />
                  <PaygapEmployeeDistribution
                    employeeDistributionData={processedData.employeeDistributionData}
                  />
                </Box>
              </Rollup>

              {/* Rollup 6: Expert Analysis */}
              <Rollup
                title={t("paygap.hub.expertAnalysis", "Expert Analysis")}
                whyText={t(
                  "paygap.hub.expertAnalysisWhy",
                  "Pay grade and position-level analysis with statistical significance testing provides the deepest insight into structural pay inequalities.",
                )}
              >
                <Box display="flex" flexDirection="column" gap={3}>
                  <PaygapPositionRoleOverview
                    data={processedData.positionRolePayGapData}
                    gradeData={processedData.gradePayGapData}
                    analysisResults={analysisResults}
                  />
                  {processedData.blinderOaxacaData.length > 0 && (
                    <PaygapBlinderOaxaca
                      data={processedData.blinderOaxacaData}
                    />
                  )}
                  <PaygapPayGradesOverview
                    positionPayGapData={processedData.seniorityPayGapData}
                    analysisResults={analysisResults}
                  />
                  <PaygapStatisticalIndicators
                    analysisSummary={processedData.analysisSummary}
                  />
                </Box>
              </Rollup>
            </Box>
          </div>

          {/* Hidden charts for PDF capture */}
          {processedData.seniorityPayGapData && processedData.seniorityPayGapData.length > 0 && (
            <Box
              id="paygap-seniority-chart-pdf"
              sx={{ position: "absolute", left: "-9999px", top: 0, width: 1000, bgcolor: "white", p: 2 }}
            >
              <BarChart
                data={processedData.seniorityPayGapData}
                yAxisLabel="%"
                xAxisLabel=""
              />
            </Box>
          )}
          {processedData.positionRolePayGapData && processedData.positionRolePayGapData.length > 0 && (
            <Box
              id="paygap-position-role-chart-pdf"
              sx={{ position: "absolute", left: "-9999px", top: 0, width: 900, bgcolor: "white", p: 2 }}
            >
              <PositionChart data={processedData.positionRolePayGapData} />
            </Box>
          )}
          {processedData.gradePayGapData && processedData.gradePayGapData.length > 0 && (
            <Box
              id="paygap-grade-chart-grading1"
              sx={{ position: "absolute", left: "-9999px", top: 0, width: 800, bgcolor: "white", p: 2 }}
            >
              <GradeChart data={processedData.gradePayGapData} selectedGradings={["I"]} />
            </Box>
          )}
        </>
      )}

      {/* ────── Drawers & Dialogs ────── */}

      {/* Employee Data Drawer */}
      <EmployeeDataDrawer
        isOpen={isEmployeeDrawerOpen}
        onClose={(): void => {
          setIsEmployeeDrawerOpen(false);
          setAutoFetchData(false);
        }}
        onDataFetched={(): void => setDataFetchedFromDatabase(true)}
        autoFetch={autoFetchData}
      />

      {/* Analysis Parameters Drawer */}
      <AnalysisParametersDrawer
        open={isAnalysisParametersOpen}
        onClose={(): void => setIsAnalysisParametersOpen(false)}
        parameters={paygapParams.parameters}
        onParameterChange={paygapParams.handleParameterChange}
        onEducationAndTrainingChange={paygapParams.handleEducationAndTrainingChange}
        onProfessionalPositionChange={paygapParams.handleProfessionalPositionChange}
        onAddEducationAndTraining={paygapParams.addEducationAndTrainingRow}
        onRemoveEducationAndTraining={paygapParams.removeEducationAndTrainingRow}
        onAddProfessionalPosition={paygapParams.addProfessionalPositionRow}
        onRemoveProfessionalPosition={paygapParams.removeProfessionalPositionRow}
        onGrading1Change={paygapParams.handleGrading1Change}
        onGrading2Change={paygapParams.handleGrading2Change}
        onGrading3Change={paygapParams.handleGrading3Change}
        onAddGrading1={paygapParams.addGrading1Row}
        onAddGrading2={paygapParams.addGrading2Row}
        onAddGrading3={paygapParams.addGrading3Row}
        onRemoveGrading1={paygapParams.removeGrading1Row}
        onRemoveGrading2={paygapParams.removeGrading2Row}
        onRemoveGrading3={paygapParams.removeGrading3Row}
        onSaveReferentials={paygapParams.saveParameters}
        hasIdConflicts={paygapParams.hasIdConflicts}
        showSaveSuccess={paygapParams.showSaveSuccess}
      />

      {/* Grade Estimator Drawer */}
      <GradeEstimatorDrawer
        open={gradeEstimator.showGradeEstimator}
        onClose={(): void => {
          gradeEstimator.setShowGradeEstimator(false);
          gradeEstimator.resetModelState();
        }}
        traitMaxValues={gradeEstimator.traitMaxValues}
        setTraitMaxValues={gradeEstimator.setTraitMaxValues}
        comboPoints={gradeEstimator.comboPoints}
        setComboPoints={gradeEstimator.setComboPoints}
        questionnaireAnswers={gradeEstimator.questionnaireAnswers}
        setQuestionnaireAnswers={gradeEstimator.setQuestionnaireAnswers}
        parentTotals={gradeEstimator.parentTotals}
        totalMaxPoints={gradeEstimator.totalMaxPoints}
        uniqueJobSeniorities={gradeEstimator.uniqueJobSeniorities}
        isLoadingPersonnel={gradeEstimator.isLoadingGetCurrentCompanyPersonnel}
        displayGrades={gradeEstimator.displayGrades}
        gradeRanges={gradeEstimator.gradeRanges}
        isModelLoading={gradeEstimator.isModelLoading}
        modelError={gradeEstimator.modelError}
        showSaveSuccess={gradeEstimator.showSaveSuccess}
        canRunEstimation={gradeEstimator.canRunEstimation}
        onRunGradeEstimation={gradeEstimator.runGradeEstimation}
        onSaveGrades={gradeEstimator.handleSaveGrades}
        onSaveSettings={gradeEstimator.handleSaveSettings}
        onSaveQuestionnaireAnswers={gradeEstimator.handleSaveQuestionnaireAnswers}
        onApplyGrades={(): void => gradeEstimator.setShowGradeOverwriteConfirm(true)}
        getGradeForPoints={gradeEstimator.getGradeForPoints}
        positionList={gradeEstimator.positionList}
        setPositionList={gradeEstimator.setPositionList}
        onSavePositionList={gradeEstimator.handleSavePositionList}
        jobTitleMatches={gradeEstimator.jobTitleMatches}
        setJobTitleMatches={gradeEstimator.setJobTitleMatches}
        matchedGrades={gradeEstimator.matchedGrades}
        setMatchedGrades={gradeEstimator.setMatchedGrades}
        onSyncPositions={gradeEstimator.handleSyncPositions}
        onSaveJobTitleMatches={gradeEstimator.handleSaveJobTitleMatches}
      />

      {/* Grade Overwrite Confirmation */}
      <GradeOverwriteDialog
        open={gradeEstimator.showGradeOverwriteConfirm}
        onClose={(): void => gradeEstimator.setShowGradeOverwriteConfirm(false)}
        onConfirm={gradeEstimator.applyGradesToEmployees}
        selectedGradingLevel={gradeEstimator.selectedGradingLevel}
        onGradingLevelChange={gradeEstimator.setSelectedGradingLevel}
      />

      {/* Auto-Populate Confirmation */}
      <AnalysisParamsOverwriteDialog
        open={autoPopulate.showOverwriteDialog}
        existingParams={autoPopulate.existingParams}
        newParams={autoPopulate.pendingParams}
        onConfirm={autoPopulate.confirmOverwrite}
        onCancel={autoPopulate.closeOverwriteDialog}
      />
    </Box>
  );
};

export default PaygapHubView;
